import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Loader2, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import type { ContainerOrderLine } from "../../types/containerOrders";
import { CONTAINER_CARGO_TYPES } from "../../constants/containerOrderConstants";
import {
  cargoTotalWeight,
  deriveVgm,
  emptyCargoRow,
  evaluateWeights,
  findCargoRowErrors,
  fromDriverCargoRows,
  issueFor,
  toCargoRows,
} from "../../utils/containerLineCargo";
import type {
  CargoRequiredField,
  CargoRowDraft,
} from "../../utils/containerLineCargo";

/**
 * What is in the box, as the driver may now write it.
 *
 * The packing list has always been dispatch's. That was the wrong owner for it
 * and the module's own comments said so without drawing the conclusion: the
 * list is finalised at the *loading site*, which is stop 2 of 3, and the person
 * standing there watching pallets go onto a forklift is the driver. It is why
 * the office's table is deliberately never frozen once a run starts — and until
 * now the one screen that could have used that latitude was the one screen
 * without a table.
 *
 * What the gap actually cost: a load that turned up as 18 pallets against a
 * booked 20 could be weighed correctly from the cab (`cargoWeightKg` has always
 * been writable here) and still be *described* wrongly, because the only way to
 * fix the description was to ring the office. The CMR is drawn off this list. So
 * the consignment note went out carrying the booking's guess, signed, while the
 * person who had counted the pallets held a phone that could see the wrong
 * number and not change it.
 *
 * ── Four fields, and why not nine ──────────────────────────────────────────
 *
 * Quantity, type, weight, description — exactly the four the office's own rule
 * requires of every row (`CARGO_REQUIRED_FIELDS`), so a row saved from here is
 * one the office's form would also accept. Volume, the three dimensions and the
 * shipping marks are left out: they come off a packing list read at a desk, and
 * a phone in one hand at a loading bay is the wrong instrument for them.
 * Omitting them is not a simplification either — the save sends only these four
 * per row, and the server patches the row rather than replacing it, so whatever
 * dispatch transcribed into the other five survives a driver correcting a count.
 *
 * ── Why a table, on a phone ────────────────────────────────────────────────
 *
 * Because the screen it sits on is already too tall, and this panel is read far
 * more often than it is written. A card per row — which is what this was —
 * spends five lines saying what four columns say in one, and three pallet types
 * then push the confirm button another screenful away from the thumb that needs
 * it. A table costs nothing to read at a glance and is the compact form of
 * exactly the same facts.
 *
 * Editing keeps the same grid and borrows the goods column for a line of its
 * own, which is the one field that cannot honestly be typed into 90px. Two
 * lines per row while editing, one while reading.
 *
 * It sits inside the stop frame between the box's figures and the paperwork,
 * because that is the order the stop happens in: read the door, check what went
 * in, take the paper, drive on.
 */

interface DriverCargoSectionProps {
  line: ContainerOrderLine;
  /** Finished, cancelled, or not this driver's turn — a record, not a form. */
  readOnly: boolean;
  busy: boolean;
  /**
   * Saves the list and answers whether it was stored.
   *
   * Returning a boolean rather than throwing, matching
   * `DriverContainerDetails.onSave`: a refused save has to leave the form open
   * with the rows still in it, because a driver whose weights were rejected has
   * to be able to see the numbers that were rejected.
   */
  onSave: (items: ReturnType<typeof fromDriverCargoRows>) => Promise<boolean>;
}

const kg = (value: number | null | undefined) =>
  value === null || value === undefined ? "—" : `${value.toLocaleString()} kg`;

/**
 * The column widths, declared once and applied through `<colgroup>`.
 *
 * ── Why a real table now ──────────────────────────────────────────────────
 *
 * This was a CSS grid with one track definition shared by the heading row and
 * the body rows, which is only the same thing as a table for as long as both
 * have the same number of cells. They did not: the editing rows carry a bin
 * button after the weight, so the four headings sat across five cells and
 * "WEIGHT" came to rest over the bin. A grid cannot catch that — it is only a
 * list of widths, and nothing in it knows that a heading belongs to a column.
 *
 * A `<table>` does know. One `<colgroup>` governs the heading, every row and
 * the total, so a cell added or removed cannot slide the headings off their
 * columns again. It also gets the semantics for free: a screen reader in a cab
 * reads "Weight, 7200" rather than two unrelated numbers.
 *
 * ── The widths ────────────────────────────────────────────────────────────
 *
 * Chosen against the values, not by eye. The quantity holds three digits
 * comfortably (a pallet count is rarely two, never five) and the weight holds
 * five, because kilogram figures on a container run to four and a half — 12,400
 * for a loaded box — and a column that clips them is a column that has to be
 * scrolled to be read. The goods description takes whatever is left and is the
 * only elastic column, which is right: it is the one field whose length is
 * genuinely unbounded.
 *
 * They add up to about 300px, which fits the content box of a 390px phone with
 * a little to spare. Narrower than that — a 360px screen, or a driver running
 * large text — and the table scrolls sideways inside its own box rather than
 * squeezing the inputs down to something untypable. See the note on the
 * scroller.
 */
const COLUMN_WIDTHS = {
  quantity: "2.9rem",
  type: "3.6rem",
  /* Elastic. `auto` with the others fixed gives it the remainder. */
  description: "auto",
  weight: "4.9rem",
  remove: "1.9rem",
} as const;

export function DriverCargoSection({
  line,
  readOnly,
  busy,
  onSave,
}: DriverCargoSectionProps) {
  const { t } = useTranslation("containerOrders");

  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<CargoRowDraft[]>([]);
  /**
   * Turned on by a rejected save, exactly as the office's table does it.
   *
   * Rows go red when the driver tries to save, not while they are still typing
   * into them — a freshly added row is empty by definition, and marking all
   * four of its boxes the instant it appears is a form shouting at somebody for
   * pressing Add.
   */
  const [showErrors, setShowErrors] = useState(false);

  /** Put the rows back to what is actually stored on the container. */
  const reseed = useCallback(() => {
    setRows(toCargoRows(line));
    setShowErrors(false);
    // Keyed on the packing list itself rather than on `line`, which is a new
    // object on every save of anything — a tare, a document, a step — and would
    // rebuild the rows each time for no reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line.cargoItems]);

  /**
   * Re-seed when the stored list moves — but never under a driver who is typing.
   *
   * Both halves matter, and the second is the one that bites. `updatedAt` moves
   * for reasons that have nothing to do with the packing list: filing a gate
   * receipt is a save, correcting a tare on the panel directly above this one
   * is a save, and a 409 refresh is a save's worth of new data arriving. All
   * three are things a driver does *while* halfway through retyping a pallet
   * count — the documents and the figures are inside the same frame as this
   * table — and a re-seed at that moment silently discards the rows they were
   * partway through entering.
   *
   * So the form is left alone while it is open. Everything that genuinely needs
   * the rows rebuilt closes it first: Cancel calls `reseed` by hand, and a
   * successful save leaves the table holding exactly what was saved.
   *
   * The same arrangement, for the same reason, as `DriverContainerDetails`.
   */
  useEffect(() => {
    if (!editing) reseed();
    // `editing` is deliberately not a dependency: leaving the form is already
    // handled by Cancel and by the save, and re-running on the transition would
    // re-seed at the one moment the rows must be left alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reseed, line.updatedAt]);

  /**
   * A container that stops being editable while the form is open.
   *
   * Reachable without the driver doing anything: dispatch cancels the
   * container, the office records the final step, or the day is reordered and
   * this box is no longer the one in front. Leaving the inputs on screen would
   * leave a save the API is going to refuse.
   */
  useEffect(() => {
    if (readOnly && editing) {
      reseed();
      setEditing(false);
    }
  }, [readOnly, editing, reseed]);

  const rowErrors = useMemo(() => findCargoRowErrors(rows), [rows]);

  const invalid = (key: string, field: CargoRequiredField) =>
    showErrors && (rowErrors[key]?.includes(field) ?? false);

  const typeOptions = useMemo(
    () =>
      CONTAINER_CARGO_TYPES.map((value) => ({
        value,
        label: t(`lineCargo.types.${value}`),
      })),
    [t],
  );

  const stored = useMemo(
    () =>
      (line.cargoItems ?? []).reduce(
        (total, item) => total + (item.weightKg ?? 0),
        0,
      ),
    [line.cargoItems],
  );

  /**
   * What the list weighs — off the rows being typed into while editing, off the
   * container otherwise.
   *
   * Live, because it is half of the VGM and the whole of what the overweight
   * check below is about: a driver adding a row needs to see the total cross
   * the limit as they add it, not after a save comes back refused.
   */
  const listedKg = editing ? cargoTotalWeight(rows) : stored;

  /**
   * Whether this list fits in the box, from the same evaluator the office page
   * uses and the mirror of the one the API enforces — so a list this screen
   * accepts is never one the save then refuses.
   *
   * The declared load is passed as stored. The driver can change it, but on the
   * details panel above rather than here, and reading it off the container is
   * what makes the two panels agree about the same container.
   */
  const weights = useMemo(() => {
    const tare = line.tareKg ?? null;
    const declared = line.cargoWeightKg ?? null;

    /**
     * The VGM, derived the way the server derives it.
     *
     * `tare + effectiveCargoKg(...)`, not `tare + listedKg`. Getting this wrong
     * was not cosmetic — it greyed out the Save button on any container that
     * had been weighed. A box with a 3,700 kg tare, a 10,000 kg bridge ticket
     * and a part-typed packing list produced a local VGM of 4,023 kg, an
     * allowance of 323 kg, and an overweight verdict against the 10,000 kg
     * declaration. The container's real VGM was 13,700 kg and nothing was
     * wrong with it: the panel had invented a contradiction out of its own
     * arithmetic and then refused the save over it, with no message, because a
     * disabled button has nowhere to put one.
     *
     * Method 2 asks what is going inside the box. It does not ask that the
     * answer be assembled from rows — a weighbridge ticket is a better answer
     * than a packing list and takes precedence over it everywhere else in the
     * platform. `deriveVgmKg` in models/ContainerOrderLine.js is the original;
     * this is its mirror, and the details panel above already had it right.
     */
    const vgmKg = deriveVgm({
      tareKg: tare,
      declaredCargoKg: declared,
      listedKg,
    });

    return evaluateWeights({
      tareKg: tare,
      maxPayloadKg: line.maxPayloadKg ?? null,
      vgmKg,
      cargoKg: listedKg,
      declaredCargoKg: declared,
    });
  }, [line.tareKg, line.maxPayloadKg, line.cargoWeightKg, listedKg]);

  /**
   * The packing list's own fault, and not the declaration's.
   *
   * `issueFor("cargoItems")` rather than `cargoIssueOf`, which answers for
   * whichever cargo figure is in force. Used only to redden the total, which is
   * this table's own figure — everything the driver is *told* comes from the
   * full list below, because a fault two fields away still stops this save and
   * saying nothing about it is how a button comes to look broken.
   */
  const cargoIssue = issueFor(weights, "cargoItems");

  /**
   * Everything currently wrong with the weights, and whose it is to fix.
   *
   * All of them, not just the packing list's. The container's four weights are
   * checked against each other as a set, so a tare heavier than the plated
   * payload refuses *this* save as surely as an overloaded list does — and the
   * earlier versions of this panel handled that badly in both directions. One
   * greyed the button for faults the driver could not fix from here and said
   * nothing; the next let the save go and spent a round trip, in a yard, to be
   * told something the screen already knew.
   *
   * So every fault is named, and each one says where the figure that has to
   * move actually lives. `cargoItems` is this table. Everything else — the
   * tare, the plated payload, the weighed load — is the details panel directly
   * above, which is close enough to point at.
   */
  const weightProblems = weights.errors.map((issue) => ({
    issue,
    /** Fixable in this table, as opposed to in the panel above it. */
    mine: issue.field === "cargoItems",
  }));

  /**
   * The weights' complaints, drawn wherever this panel is drawn.
   *
   * One element, two placements: above the buttons while editing, where the
   * driver is looking when they reach for Save, and under the table while
   * reading, where it is the only thing that would otherwise say a finished
   * container does not add up. Written once so the two cannot drift into saying
   * different things about the same container.
   */
  const weightNotices = weightProblems.length > 0 && (
    <div className="space-y-1">
      {weightProblems.map(({ issue, mine }) => (
        <p
          key={`${issue.code}:${issue.field}`}
          className="flex items-start gap-1.5 rounded-md bg-[#FBEDED] px-2 py-1.5 text-[11px] text-[#A63A39]"
        >
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            {t(`lineWeights.${issue.code}`, issue.values)}
            {/* Where to go, for the ones that are not this table's. The message
                itself names a field — "tare", "max payload" — and a driver who
                has just scrolled to a packing list should not have to work out
                which of the panels above carries it. */}
            {!mine && ` ${t("driver.cargoFixAbove")}`}
          </span>
        </p>
      ))}
    </div>
  );

  const update = (key: string, field: CargoRequiredField, value: string) =>
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, [field]: value } : row)),
    );

  const cancelEdit = () => {
    reseed();
    setEditing(false);
  };

  const save = async () => {
    /*
     * The row rule first, and the save is not attempted when it fails. The
     * server holds the identical rule, but its message names a row by index —
     * "cargo row 3" — and a driver looking at a table on a phone has to count
     * to find out which. Marking the boxes is the same refusal, said where the
     * fix is.
     */
    if (Object.keys(rowErrors).length) {
      setShowErrors(true);
      return;
    }

    /*
     * The weights, checked here rather than by grey-ing the button.
     *
     * The notices are already on screen above this button, so nothing new is
     * being revealed — but a press that silently does nothing reads as a broken
     * app, and that is exactly what a disabled button is from the far side of a
     * windscreen. The toast is the acknowledgement: the press was received, and
     * here is the reason it went no further.
     *
     * Not sent to the server either. It would refuse the identical set of rules
     * (`evaluateContainerWeights` is the original this mirrors), so the round
     * trip buys nothing and costs a driver in a yard with one bar the wait, or
     * a network error instead of an answer.
     */
    if (weightProblems.length) {
      const { issue, mine } = weightProblems[0];
      toast.error(
        `${t(`lineWeights.${issue.code}`, issue.values)}${
          mine ? "" : ` ${t("driver.cargoFixAbove")}`
        }`,
      );
      return;
    }

    const saved = await onSave(fromDriverCargoRows(rows));
    if (saved) {
      setShowErrors(false);
      setEditing(false);
    }
  };

  const items = line.cargoItems ?? [];

  const inputClass = (row: CargoRowDraft, name: CargoRequiredField) =>
    /*
     * `[appearance:textfield]` and the two spin-button rules kill the stepper
     * arrows Chrome and Safari put inside a number input. They are 16px of
     * furniture nobody taps on a touchscreen — the keypad is how a figure gets
     * typed — and in a 46px column they were eating a third of the space the
     * digits needed. Removing them is what lets the quantity show three digits
     * and the weight five.
     */
    `w-full min-w-0 rounded-md border px-1 py-1.5 text-[12px] text-[#1A1A18] outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
      invalid(row.key, name)
        ? "border-[#E8B4B3] bg-[#FBEDED]"
        : "border-[#D8D6CE] bg-white focus:border-[#BA7517]"
    }`;

  /**
   * The column widths, as the table's own definition of them.
   *
   * `withRemove` adds the bin column, which only the editing table has. It is
   * the whole reason this is a `<colgroup>` and not a shared grid class: the
   * two tables genuinely have different column counts, and a table is the only
   * arrangement where that cannot push the headings out of line.
   */
  const columns = (withRemove: boolean) => (
    <colgroup>
      <col style={{ width: COLUMN_WIDTHS.quantity }} />
      <col style={{ width: COLUMN_WIDTHS.type }} />
      <col style={{ width: COLUMN_WIDTHS.description }} />
      <col style={{ width: COLUMN_WIDTHS.weight }} />
      {withRemove && <col style={{ width: COLUMN_WIDTHS.remove }} />}
    </colgroup>
  );

  const headCell = "px-1 py-1 text-left font-medium";

  const heading = (withRemove: boolean) => (
    <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-wide text-[#A6A49C]">
      <tr>
        <th scope="col" className={headCell}>
          {t("lineCargo.columns.qty")}
        </th>
        <th scope="col" className={headCell}>
          {t("lineCargo.columns.type")}
        </th>
        <th scope="col" className={headCell}>
          {t("lineCargo.columns.description")}
        </th>
        {/* Left, like every other heading, and that is the point rather than a
            detail. This column was the one exception — heading right-aligned
            because the kilogram figures under it were — and with a bin column
            after it the word ended up floating between two columns, reading as
            the heading of neither. Four columns that all start at the same edge
            as their contents is a table a driver can scan down; one that does
            not is a table they have to decode.

            "Weight", not "Weight (kg)": the unit is already on every value in
            the column, and the long form does not fit at this size — it is the
            office table's label, written for a desk. The inputs keep it as
            their accessible name, where length costs nothing. */}
        <th scope="col" className={headCell}>
          {t("driver.cargoColWeight")}
        </th>
        {/* Headed, but silently: the column holds a bin, and a word printed
            over an icon that already says what it does is noise in 30px. The
            cell itself has to exist — a missing heading is what put "WEIGHT"
            over the bin in the first place — and the label survives for a
            screen reader, which has no icon to go on. */}
        {withRemove && (
          <th scope="col" className={headCell}>
            <span className="sr-only">{t("lineCargo.columns.action")}</span>
          </th>
        )}
      </tr>
    </thead>
  );

  /**
   * The sideways escape hatch.
   *
   * The columns add up to about 300px, which fits a 390px phone. A 360px
   * screen, a driver running large text, or a locale whose headings are longer
   * than the English ones will all overrun it — and the alternative to scrolling
   * is squeezing a weight field down to two visible digits, which is worse: a
   * driver cannot check a figure they cannot see.
   *
   * Scoped to the table rather than the page. The panel, the frame and the page
   * behind it all stay put; only the columns move, which is the behaviour a
   * table is expected to have and the only one that keeps the heading over its
   * column while it happens.
   */
  const scroller = "overflow-x-auto";

  return (
    <div className="rounded-[10px] bg-white p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-[12px] font-medium text-[#6B6A64]">
          {t("driver.cargo")}
        </p>
        {/* The one control, and only when there is something it could do.
            Inline with the heading rather than under the table: a full-width
            button below the rows is another line of vertical space on the
            screen this panel was rebuilt to shorten. */}
        {!readOnly && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="flex items-center gap-1 rounded-full border border-[#BA7517] px-2.5 py-1 text-[11px] font-semibold text-[#BA7517] active:bg-[#FAEEDA]"
          >
            <Pencil className="h-3 w-3" />
            {t("driver.edit")}
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-1.5">
          <div className={scroller}>
            <table className="w-full min-w-[19rem] table-fixed border-collapse">
              {columns(true)}
              {heading(true)}
              <tbody>
                {rows.map((row) => (
                  /*
                   * Two rows per item: the four boxes, then the row's own
                   * complaint when it has one. A `<tr>` cannot be given a
                   * border-radius or a background that reads as a card, so the
                   * grouping is done with a top border on all but the first —
                   * which is what a table is supposed to look like anyway, and
                   * one line of furniture per row lighter than the bordered
                   * cards this replaced.
                   */
                  <Fragment key={row.key}>
                    <tr className="align-middle">
                      <td className="px-1 py-1">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          /* `numeric` and not `decimal`: a count is whole, and
                             a keypad offering a separator invites a half
                             pallet. Stated as `inputMode` rather than left to
                             `type="number"`, which Android treats as a hint. */
                          inputMode="numeric"
                          aria-label={t("lineCargo.columns.qty")}
                          value={row.quantity}
                          onChange={(e) =>
                            update(row.key, "quantity", e.target.value)
                          }
                          className={inputClass(row, "quantity")}
                        />
                      </td>
                      <td className="px-1 py-1">
                        <select
                          aria-label={t("lineCargo.columns.type")}
                          value={row.type}
                          onChange={(e) =>
                            update(row.key, "type", e.target.value)
                          }
                          className={`${inputClass(row, "type")} px-0.5`}
                        >
                          <option value="">—</option>
                          {typeOptions.map((option) => (
                            /* The code, not the label. The column is 58px wide
                               and the codes are what the packing list and the
                               CMR both print; the long name would truncate to
                               the same three letters anyway, with a tooltip
                               nobody on a phone can reach. */
                            <option key={option.value} value={option.value}>
                              {option.value}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-1 py-1">
                        <input
                          type="text"
                          aria-label={t("lineCargo.columns.description")}
                          value={row.description}
                          onChange={(e) =>
                            update(row.key, "description", e.target.value)
                          }
                          className={inputClass(row, "description")}
                        />
                      </td>
                      <td className="px-1 py-1">
                        <input
                          type="number"
                          min={0}
                          inputMode="decimal"
                          aria-label={t("lineCargo.columns.weightKg")}
                          value={row.weightKg}
                          onChange={(e) =>
                            update(row.key, "weightKg", e.target.value)
                          }
                          className={inputClass(row, "weightKg")}
                        />
                      </td>
                      <td className="px-0 py-1">
                        {/* In its own column now rather than tucked beside the
                            weight. That was what pushed "WEIGHT" off its
                            heading, and it also made the two the same tap
                            target as far as a thumb was concerned. */}
                        <button
                          type="button"
                          onClick={() =>
                            setRows((current) =>
                              current.filter((other) => other.key !== row.key),
                            )
                          }
                          aria-label={t("lineCargo.removeItem")}
                          className="flex h-8 w-full items-center justify-center rounded text-[#A63A39] active:bg-[#FBEDED]"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>

                    {/* What is missing, named once per row rather than under
                        each of four boxes — four red captions under one row is
                        more height than the row itself. Spans the whole table
                        so a long list of field names wraps rather than
                        widening a column. */}
                    {showErrors && rowErrors[row.key]?.length > 0 && (
                      <tr>
                        <td colSpan={5} className="px-1 pb-1 text-[10px] text-[#A63A39]">
                          {t("driver.cargoRowMissing", {
                            fields: rowErrors[row.key]
                              .map((field) =>
                                t(
                                  `lineCargo.columns.${
                                    field === "quantity" ? "qty" : field
                                  }`,
                                ),
                              )
                              .join(", "),
                          })}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            onClick={() => setRows((current) => [...current, emptyCargoRow()])}
            className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-[#C9C7BE] bg-[#F7F6F2] py-1.5 text-[11px] font-semibold text-[#6B6A64]"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={3} />
            {t("lineCargo.addItem")}
          </button>

          <div className="flex items-baseline justify-between rounded-md bg-[#F7F6F2] px-2 py-1.5">
            <span className="text-[11px] text-[#6B6A64]">
              {t("lineCargo.totalWeight")}
            </span>
            <span
              className={`text-[12px] font-bold ${
                cargoIssue ? "text-[#A63A39]" : "text-[#1A1A18]"
              }`}
            >
              {kg(listedKg)}
            </span>
          </div>

          {/* Above the buttons, not below them. The earlier version printed this
              under the whole panel, which on a phone meant the explanation for
              a refused save was off-screen at the moment the driver reached for
              Save. */}
          {weightNotices}

          <div className="flex gap-2 pt-0.5">
            <button
              type="button"
              onClick={cancelEdit}
              disabled={busy}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-[24px] border border-[#D8D6CE] px-3 py-2.5 text-[12px] font-semibold text-[#6B6A64] disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
              {t("lineEdit.cancel")}
            </button>
            <button
              type="button"
              onClick={save}
              /*
               * Live unless a save is actually in flight. Nothing else greys
               * this button, and that is a deliberate reversal.
               *
               * A disabled button cannot explain itself. There is no tooltip on
               * a touchscreen, no focus, no click to respond to — so the only
               * thing a driver learns from one is that the app will not do what
               * they asked, which is indistinguishable from the app being
               * broken. Both earlier versions of this got it wrong in
               * different ways: the first greyed out for faults the driver
               * could not even fix from this table, and the second still greyed
               * out with its only explanation printed *below* the button.
               *
               * So every refusal is now a refusal with a sentence attached —
               * incomplete rows mark the boxes they are missing, contradictory
               * weights raise the message naming the figure that has to move,
               * and both leave the press visibly acknowledged. That is the rule
               * the row check already followed; the weights simply had not been
               * brought into line with it.
               */
              disabled={busy}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-[24px] bg-[#BA7517] px-3 py-2.5 text-[12px] font-semibold text-white disabled:opacity-50"
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              {t("driver.saveDetails")}
            </button>
          </div>
        </div>
      ) : items.length === 0 ? (
        <p className="rounded-md bg-[#F7F6F2] px-2 py-2 text-center text-[11px] text-[#A6A49C]">
          {t(readOnly ? "lineCargo.emptyReadOnly" : "driver.cargoEmptyHint")}
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border border-[#EFEDE6]">
          <div className={scroller}>
            <table className="w-full min-w-[17rem] table-fixed border-collapse">
              {columns(false)}
              {heading(false)}
              <tbody>
                {items.map((item, index) => (
                  <tr
                    key={item._id ?? index}
                    className={`text-[12px] ${
                      index > 0 ? "border-t border-[#F2F0E9]" : ""
                    }`}
                  >
                    <td className="px-1 py-1.5 font-semibold text-[#1A1A18]">
                      {(item.quantity ?? 0).toLocaleString()}
                    </td>
                    <td className="px-1 py-1.5 text-[#6B6A64]">
                      {String(item.type ?? "").trim() || "—"}
                    </td>
                    {/* Truncated, not wrapped. A row that grows to three lines
                        for a long description puts the table back to the height
                        the cards had; the full text is one tap away in the
                        editor. */}
                    <td
                      className="truncate px-1 py-1.5 text-[#1A1A18]"
                      title={item.description}
                    >
                      {String(item.description ?? "").trim() || "—"}
                    </td>
                    <td className="whitespace-nowrap px-1 py-1.5 font-medium text-[#1A1A18]">
                      {kg(item.weightKg)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-[#EFEDE6] bg-[#F7F6F2] text-[12px]">
                  {/* Spans everything but the figure, so the total sits under
                      the column it is a total of. */}
                  <td colSpan={3} className="px-1 py-1.5 text-[11px] text-[#6B6A64]">
                    {t("lineCargo.totalWeight")}
                  </td>
                  <td
                    className={`whitespace-nowrap px-1 py-1.5 font-bold ${
                      cargoIssue ? "text-[#A63A39]" : "text-[#1A1A18]"
                    }`}
                  >
                    {kg(stored)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* The same notices while reading, where they are the only thing that
          would otherwise say a container does not add up. A finished run is
          looked at precisely because somebody rang about one of these figures. */}
      {!editing && <div className="mt-1.5">{weightNotices}</div>}
    </div>
  );
}
